import { useAuthorizedRequest } from '@/hooks/useRequest';
import { apiRequest } from '@/lib/api';
import type { TeacherRate, TeacherRateListResponse } from '@/lib/TeacherRate';
import type { Teacher } from '@/lib/Teacher';
import type { Subject } from '@/lib/Subject';
import { t } from '@/lib/i18n';
import {
    Box,
    Button,
    Drawer,
    Field,
    Heading,
    HStack,
    Input,
    Portal,
    Skeleton,
    Table,
    Text,
    VStack,
    CloseButton,
} from '@chakra-ui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';

function jsonFetcher(url: string, token: string) {
    return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
    });
}

export function TeacherRates() {
    const { getAccessTokenSilently } = useAuth0();

    const { data: teachersData, error: teachersError, isLoading: teachersLoading, isAuthenticated } =
        useAuthorizedRequest<{ teachers: Teacher[] }>('/teachers', jsonFetcher);
    const { data: subjectsData, error: subjectsError, isLoading: subjectsLoading } =
        useAuthorizedRequest<{ subjects: Subject[] }>('/subjects', jsonFetcher);

    const teachers = useMemo(() => (teachersData?.teachers ?? []).filter(t => !t.deleted).sort((a, b) => a.name.localeCompare(b.name)), [teachersData]);
    const subjects = useMemo(() => (subjectsData?.subjects ?? []).filter(s => !s.deleted).sort((a, b) => a.name.localeCompare(b.name)), [subjectsData]);

    // Fetch rates for all teachers using useEffect (can't use useAuthorizedRequest in a loop)
    const [ratesByTeacher, setRatesByTeacher] = useState<Record<string, TeacherRate[]>>({});
    const [ratesLoading, setRatesLoading] = useState(false);
    const fetchIdRef = useRef(0);

    useEffect(() => {
        if (!isAuthenticated || teachers.length === 0) return;

        let cancelled = false;
        const fetchId = ++fetchIdRef.current;

        async function fetchAllRates() {
            setRatesLoading(true);
            try {
                const token = await getAccessTokenSilently();
                const results: Record<string, TeacherRate[]> = {};

                for (const t of teachers) {
                    if (cancelled) return;
                    try {
                        const resp = await jsonFetcher(`/teacher-rates/${t.id}`, token) as TeacherRateListResponse;
                        results[t.id] = resp.rates ?? [];
                    } catch {
                        results[t.id] = [];
                    }
                }

                if (!cancelled && fetchId === fetchIdRef.current) {
                    setRatesByTeacher(results);
                }
            } finally {
                if (!cancelled && fetchId === fetchIdRef.current) {
                    setRatesLoading(false);
                }
            }
        }

        fetchAllRates();
        return () => { cancelled = true; };
    }, [isAuthenticated, teachers, getAccessTokenSilently]);

    // Build lookup: teacher_id -> Map<subject, TeacherRate> (latest rate per subject)
    const rateMap = useMemo(() => {
        const map: Record<string, Record<string, TeacherRate>> = {};
        for (const [teacherId, rates] of Object.entries(ratesByTeacher)) {
            const subjectMap: Record<string, TeacherRate> = {};
            for (const rate of rates) {
                const existing = subjectMap[rate.subject];
                if (!existing || rate.start_date > existing.start_date) {
                    subjectMap[rate.subject] = rate;
                }
            }
            map[teacherId] = subjectMap;
        }
        return map;
    }, [ratesByTeacher]);

    const getRate = useCallback((teacherId: string, subject: string): TeacherRate | null => {
        return rateMap[teacherId]?.[subject] ?? null;
    }, [rateMap]);

    // Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editTeacherId, setEditTeacherId] = useState('');
    const [editTeacherName, setEditTeacherName] = useState('');
    const [editSubject, setEditSubject] = useState('');
    const [editRate, setEditRate] = useState<number>(0);
    const [editStartDate, setEditStartDate] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');

    const openDrawer = useCallback((teacherId: string, teacherName: string, subject: string) => {
        const existing = getRate(teacherId, subject);
        setEditTeacherId(teacherId);
        setEditTeacherName(teacherName);
        setEditSubject(subject);
        setEditRate(existing?.rate_uah ?? 0);
        setEditStartDate(existing?.start_date ?? new Date().toISOString().slice(0, 10));
        setSaveMessage('');
        setDrawerOpen(true);
    }, [getRate]);

    async function handleSave() {
        setSaving(true);
        setSaveMessage('');
        try {
            const token = await getAccessTokenSilently();

            const existingRates = ratesByTeacher[editTeacherId] ?? [];
            const mergedRates = new Map<string, { subject: string; rate_uah: number; start_date: string }>();

            for (const r of existingRates) {
                if (r.subject !== editSubject) {
                    mergedRates.set(r.subject, { subject: r.subject, rate_uah: r.rate_uah, start_date: r.start_date });
                }
            }
            if (editRate > 0) {
                mergedRates.set(editSubject, { subject: editSubject, rate_uah: editRate, start_date: editStartDate });
            }

            const ratesArray = Array.from(mergedRates.values());

            await apiRequest('PUT', `/teacher-rates/${editTeacherId}`, token, { rates: ratesArray });

            setSaveMessage(t('price_matrix.saved'));
            setDrawerOpen(false);

            // Refresh rates for this teacher
            const resp = await jsonFetcher(`/teacher-rates/${editTeacherId}`, token) as TeacherRateListResponse;
            setRatesByTeacher(prev => ({ ...prev, [editTeacherId]: resp.rates ?? [] }));
        } catch (e) {
            setSaveMessage(`${t('label.error')}: ${e instanceof Error ? e.message : String(e)}`);
        } finally {
            setSaving(false);
        }
    }

    if (!isAuthenticated) return <div>{t('label.needs_auth')}</div>;
    if (teachersError || subjectsError) return <div>{t('label.error')}: {(teachersError || subjectsError)!.message}</div>;

    if (teachersLoading || subjectsLoading) {
        return (
            <VStack gap={4} align="stretch">
                <Heading size="md">{t('teacher_rates.title')}</Heading>
                <Box overflowX="auto">
                    <Table.Root variant="line" showColumnBorder interactive size="sm">
                        <Table.Header>
                            <Table.Row>
                                <Table.ColumnHeader>{t('label.teacher')}</Table.ColumnHeader>
                                {[...Array(Math.max(subjects.length, 3))].map((_, j) => (
                                    <Table.ColumnHeader key={j} textAlign="center">
                                        <Skeleton height="16px" />
                                    </Table.ColumnHeader>
                                ))}
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {[...Array(5)].map((_, i) => (
                                <Table.Row key={i}>
                                    <Table.Cell><Skeleton height="20px" /></Table.Cell>
                                    {[...Array(Math.max(subjects.length, 3))].map((_, j) => (
                                        <Table.Cell key={j} textAlign="center">
                                            <Skeleton height="20px" />
                                        </Table.Cell>
                                    ))}
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table.Root>
                </Box>
            </VStack>
        );
    }

    if (teachers.length === 0 || subjects.length === 0) {
        return (
            <VStack gap={4} align="stretch">
                <Heading size="md">{t('teacher_rates.title')}</Heading>
                <Text color="gray.500">{t('price_matrix.no_data')}</Text>
            </VStack>
        );
    }

    return (
        <VStack gap={4} align="stretch">
            <Heading size="md">{t('teacher_rates.title')}</Heading>

            <Box overflowX="auto">
                <Table.Root variant="line" showColumnBorder interactive size="sm">
                    <Table.Header>
                        <Table.Row>
                            <Table.ColumnHeader>{t('label.teacher')}</Table.ColumnHeader>
                            {subjects.map(s => (
                                <Table.ColumnHeader key={s.id} textAlign="center">
                                    {s.name}
                                </Table.ColumnHeader>
                            ))}
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {ratesLoading ? (
                            [...Array(Math.min(teachers.length, 5))].map((_, i) => (
                                <Table.Row key={i}>
                                    <Table.Cell><Skeleton height="20px" /></Table.Cell>
                                    {subjects.map(s => (
                                        <Table.Cell key={s.id} textAlign="center">
                                            <Skeleton height="20px" />
                                        </Table.Cell>
                                    ))}
                                </Table.Row>
                            ))
                        ) : teachers.map(teacher => (
                            <Table.Row key={teacher.id}>
                                <Table.Cell fontWeight="medium">{teacher.name}</Table.Cell>
                                {subjects.map(subject => {
                                    const rate = getRate(teacher.id, subject.name);
                                    return (
                                        <Table.Cell
                                            key={subject.id}
                                            textAlign="center"
                                            cursor="pointer"
                                            onClick={() => openDrawer(teacher.id, teacher.name, subject.name)}
                                            _hover={{ bg: 'gray.100' }}
                                            p={2}
                                        >
                                            {rate ? (
                                                <VStack gap={0}>
                                                    <Text fontSize="sm" fontWeight="bold">{rate.rate_uah} ₴</Text>
                                                    <Text fontSize="xs" color="gray.500">{rate.start_date}</Text>
                                                </VStack>
                                            ) : (
                                                <Text color="gray.400">—</Text>
                                            )}
                                        </Table.Cell>
                                    );
                                })}
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table.Root>
            </Box>

            {/* Edit Drawer */}
            <Drawer.Root open={drawerOpen} onOpenChange={(e) => setDrawerOpen(e.open)} placement="end" size="sm">
                <Portal>
                    <Drawer.Backdrop />
                    <Drawer.Positioner>
                        <Drawer.Content>
                            <Drawer.Header>
                                <Drawer.Title>{t('teacher_rates.title')}</Drawer.Title>
                                <Drawer.CloseTrigger asChild>
                                    <CloseButton size="xs" variant="solid" />
                                </Drawer.CloseTrigger>
                            </Drawer.Header>
                            <Drawer.Body>
                                <VStack gap={4} align="stretch">
                                    <Text fontWeight="medium">{editTeacherName} — {editSubject}</Text>

                                    <Field.Root>
                                        <Field.Label>{t('label.subject')}</Field.Label>
                                        <Input value={editSubject} disabled />
                                    </Field.Root>

                                    <Field.Root>
                                        <Field.Label>{t('teacher_rates.rate')} (₴)</Field.Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            value={editRate}
                                            onChange={e => setEditRate(parseInt(e.target.value) || 0)}
                                        />
                                    </Field.Root>

                                    <Field.Root>
                                        <Field.Label>{t('teacher_rates.start_date')}</Field.Label>
                                        <Input
                                            type="date"
                                            value={editStartDate}
                                            onChange={e => setEditStartDate(e.target.value)}
                                        />
                                    </Field.Root>

                                    {saveMessage && (
                                        <Text fontSize="sm" color={saveMessage.includes('error') || saveMessage.includes('Error') ? 'red.500' : 'green.500'}>
                                            {saveMessage}
                                        </Text>
                                    )}

                                    <HStack gap={2} justify="flex-end">
                                        <Button variant="outline" onClick={() => setDrawerOpen(false)}>
                                            {t('action.cancel')}
                                        </Button>
                                        <Button onClick={handleSave} loading={saving}>
                                            {t('action.save')}
                                        </Button>
                                    </HStack>
                                </VStack>
                            </Drawer.Body>
                        </Drawer.Content>
                    </Drawer.Positioner>
                </Portal>
            </Drawer.Root>
        </VStack>
    );
}
